import {
  ICampaignEntryReportResult,
  IMarketingCampaignEntry,
  IMarketingCampaignRun,
  IMarketingCampaignStep,
  IPatient,
  MarketingCampaignEntryStatus,
  MarketingCampaignRunType,
  MarketingCampaignType,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  UniqueCounter,
  WithRef,
  asReffable,
  toNamedDocument,
} from '@principle-theorem/shared';
import { isEmail } from '../patient/patient-contact-details';
import { normaliseEmail } from '../../helpers';
import { MarketingCampaignEntry } from './campaign-entry';
import { MarketingCampaignRun } from './campaign-run';

/**
 * Normalise and validate an email. Returns `undefined` for
 * empty/invalid values so callers get a clean, storable value or nothing.
 */
export function normaliseAndValidateEmail(
  email: string | undefined
): string | undefined {
  if (!email) {
    return undefined;
  }
  const normalised = normaliseEmail(email);
  if (!isEmail(normalised)) {
    return undefined;
  }
  return normalised;
}

/**
 * Trim a phone number string. Returns `undefined` for
 * empty/whitespace-only values.
 */
export function normalisePhone(phone: string | undefined): string | undefined {
  const trimmed = phone?.trim();
  if (!trimmed) {
    return undefined;
  }
  return trimmed;
}

export class UniqueEntryCheck {
  email = new UniqueCounter();
  phone = new UniqueCounter();

  constructor(entries: ICampaignEntryReportResult[]) {
    this.addResults(entries);
  }

  addResults(results: ICampaignEntryReportResult[]): void {
    for (const result of results) {
      const email = normaliseAndValidateEmail(result.patientEmail);
      const phone = normalisePhone(result.patientMobile);
      if (email) {
        this.email.add(email);
      }
      if (phone) {
        this.phone.add(phone);
      }
    }
  }
}

export interface ICampaignEntryEnrolment {
  result: ICampaignEntryReportResult;
  hasDuplicateEmail: boolean;
  hasDuplicateMobile: boolean;
  normalisedEmail?: string;
  normalisedMobile?: string;
  autoApprove: boolean;
  status: MarketingCampaignEntryStatus;
}

export class CampaignEntryEnrolment {
  static evaluateEntries(
    results: ICampaignEntryReportResult[],
    runType: MarketingCampaignRunType,
    remainingEntries?: number
  ): ICampaignEntryEnrolment[] {
    // TODO: How do we check for a duplicate already in the campaign?
    // EG. they have removed an entry because it has a duplicate phone or email
    // the other is added as an entry, it gets stuck in preview and carried over
    // to this run. The report identifies the previously removed entry again, but
    // this time there isn't a duplicate in the report (its skipped due to
    // re-entry rules), but its in the campaign run as a preview entry.
    const check = new UniqueEntryCheck(results);

    const evaluations = results.map((result) => {
      const normalisedEmail = normaliseAndValidateEmail(result.patientEmail);
      const normalisedMobile = normalisePhone(result.patientMobile);
      const hasDuplicateEmail = normalisedEmail
        ? check.email.isDuplicate(normalisedEmail)
        : false;
      const hasDuplicateMobile = normalisedMobile
        ? check.phone.isDuplicate(normalisedMobile)
        : false;
      const autoApprove =
        runType !== MarketingCampaignRunType.Manual &&
        !hasDuplicateMobile &&
        !hasDuplicateEmail;
      return {
        result,
        hasDuplicateEmail,
        hasDuplicateMobile,
        normalisedEmail,
        normalisedMobile,
        autoApprove,
        status: MarketingCampaignEntryStatus.Preview,
      };
    });

    const canApprove = evaluations.filter((enrolment) => enrolment.autoApprove);
    const cantApprove = evaluations.filter(
      (enrolment) => !enrolment.autoApprove
    );

    const approveCount = this.clampApproveCount(
      0,
      canApprove.length,
      remainingEntries
    );
    const willApprove = canApprove.slice(0, approveCount);
    const wontApprove = canApprove.slice(approveCount);

    const approved = willApprove.map((enrolment) => {
      enrolment.status = MarketingCampaignEntryStatus.Approved;
      return enrolment;
    });

    return [approved, wontApprove, cantApprove].flat();
  }

  static clampApproveCount(min: number, max: number, count?: number): number {
    if (count === undefined) {
      return max;
    }
    if (count < min) {
      return min;
    }
    if (count > max) {
      return max;
    }
    return count;
  }

  static toEntry(
    enrolment: ICampaignEntryEnrolment,
    campaignRunRef: DocumentReference<IMarketingCampaignRun>,
    activeSteps: DocumentReference<IMarketingCampaignStep>[],
    patient: WithRef<IPatient>
  ): IMarketingCampaignEntry {
    return MarketingCampaignEntry.init({
      patient: toNamedDocument(patient),
      campaignRunRef: campaignRunRef,
      enrolledByCampaignRunRef: campaignRunRef,
      campaignRef: MarketingCampaignRun.campaignRef(asReffable(campaignRunRef)),
      scopeLabel: enrolment.result.scopeLabel,
      scopeData: enrolment.result.scopeData,
      steps: activeSteps,
      status: enrolment.status,
      hasDuplicateEmail: enrolment.hasDuplicateEmail,
      hasDuplicateMobile: enrolment.hasDuplicateMobile,
      normalisedEmail: enrolment.normalisedEmail,
      normalisedMobile: enrolment.normalisedMobile,
    });
  }

  static async resolvePatient(
    enrolment: ICampaignEntryEnrolment
  ): Promise<WithRef<IPatient> | undefined> {
    const patient = await Firestore.safeGetDoc(enrolment.result.patientRef);
    if (!patient) {
      // eslint-disable-next-line no-console
      console.warn(
        `Patient not found for entry: ${enrolment.result.patientName} (${enrolment.result.patientRef.path})`
      );
      return;
    }
    return patient;
  }

  static verifyEnrolment(
    patient: WithRef<IPatient>,
    campaignType: MarketingCampaignType
  ): boolean {
    if (
      campaignType === MarketingCampaignType.Marketing &&
      patient.metadata?.receiveMarketing === false
    ) {
      return false;
    }
    return true;
  }
}
